import { uploadManager } from './uploadManager'
import { generateFramedVideo, generateLiveMode, PhotoState } from './generateFramedVideo'
import { FrameConfig } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { saveMediaOffline } from './offlineStorage'

/**
 * Timeline speed when re-encoding pre-recorded blobs into story/live framed MP4s.
 * Must stay 1: input `recordingUrl` clips already reflect capture `recordingSpeed`;
 * using that speed again here made output duration ≈ sourceDuration / recordingSpeed
 * (e.g. 3s clip + playback rate 3 → ~1s export).
 */
const FRAMED_EXPORT_RECORDING_SPEED = 1

/**
 * Check real network connectivity status
 */
async function checkNetworkStatus(): Promise<boolean> {
  if (typeof window === 'undefined') return true
  const bridge = window as typeof window & { api?: { getInternetStatus?: () => Promise<boolean> } }
  if (bridge.api?.getInternetStatus) {
    return await bridge.api.getInternetStatus()
  }
  return navigator.onLine
}

/**
 * Check if preload API (saveFile) is available
 */
function hasPreloadAPI(): boolean {
  if (typeof window === 'undefined') return false
  const bridge = window as typeof window & { api?: { saveFile?: unknown } }
  return Boolean(bridge.api?.saveFile)
}

interface VideoProcessingTask {
  id: string
  type: 'session' | 'story' | 'live'
  resources: Array<{
    photoUrl: string
    videoUrl: string
  }>
  frameConfig: FrameConfig
  sessionId: string
  userId: string
  projectId: string
  galleryId: string
  videoFile?: File
  speed?: number
  fps?: number
  photoStates?: Record<string, PhotoState>
  countdown?: number
  photoDuration?: number
  onComplete?: (uploadedUrl: string, localUrl?: string) => void
  onError?: (error: Error) => void
}

class BackgroundVideoProcessor {
  private static instance: BackgroundVideoProcessor
  private processingQueue: VideoProcessingTask[] = []
  private isProcessing: boolean = false
  private activeTasks: Set<string> = new Set()

  static getInstance(): BackgroundVideoProcessor {
    if (!this.instance) {
      this.instance = new BackgroundVideoProcessor()
    }
    return this.instance
  }

  async processVideo(task: VideoProcessingTask): Promise<void> {
    console.log('Starting video processing task:', task.id)
    this.processingQueue.push(task)
    if (!this.isProcessing) {
      this.processNextTask()
    }
  }

  isTaskProcessing(taskId: string): boolean {
    const isProcessing = this.activeTasks.has(taskId)
    console.log(`Checking task ${taskId} processing status:`, isProcessing)

    return isProcessing
  }

  hasActiveVideoTasks(): boolean {
    const hasActive =
      this.activeTasks.size > 0 || this.processingQueue.length > 0 || this.isProcessing
    console.log(`Checking for any active video tasks:`, hasActive, {
      activeTasksCount: this.activeTasks.size,
      queueLength: this.processingQueue.length,
      isProcessing: this.isProcessing
    })

    return hasActive
  }

  private async downloadVideoLocally(
    videoUrl: string,
    taskId: string,
    taskType: 'session' | 'story' | 'live',
    sessionId: string
  ): Promise<void> {
    try {
      const { downloadLocal } = useSettingsStore.getState()
      console.log('Download settings:', { downloadLocal, taskId, taskType, videoUrl })

      if (!downloadLocal) {
        console.log('Download skipped: downloadLocal setting is false')
        return
      }

      // Only download strip videos
      if (taskType !== 'story' && taskType !== 'live') {
        useSessionStateStore.getState().logActivity(
          `Video download skipped for task ${taskId}: not a strip or live video`,
          {
            taskId,
            taskType,
            reason: 'Not a strip or live video type'
          },
          undefined,
          sessionId
        )
        console.log('Download skipped: not a strip or live video (type is not "strip" or "live")')
        return
      }

      console.log('Starting strip video download for task:', taskId)

      // Use fetch to get the video blob
      const response = await fetch(videoUrl)
      if (!response.ok) {
        const errorMsg = `Failed to fetch video for download: ${response.status} ${response.statusText}`
        useSessionStateStore.getState().logActivity(
          `Video download fetch failed for task ${taskId}: ${errorMsg}`,
          {
            taskId,
            taskType,
            responseStatus: response.status,
            responseStatusText: response.statusText,
            level: 'error'
          },
          undefined,
          sessionId
        )
        console.error('Failed to fetch video for download:', response.status, response.statusText)
        return
      }

      const blob = await response.blob()
      console.log('Video blob received:', { size: blob.size, type: blob.type })

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob)

      // Create a temporary link element
      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = `dreambooth-${taskId}.mp4`

      // Append to body, click, and cleanup
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(blobUrl)

      console.log('Strip video downloaded successfully for task:', taskId)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      useSessionStateStore.getState().logActivity(
        `Error downloading strip video locally for task ${taskId}: ${errorMsg}`,
        {
          taskId,
          taskType,
          error: errorMsg,
          level: 'error'
        },
        undefined,
        sessionId
      )
      console.error('Error downloading strip video locally:', error)
    }
  }

  private async processNextTask(): Promise<void> {
    if (this.processingQueue.length === 0) {
      console.log('No more tasks in queue')
      this.isProcessing = false
      return
    }

    this.isProcessing = true
    const task = this.processingQueue.shift()
    if (!task) return

    console.log('Processing task:', task.id, { type: task.type })
    this.activeTasks.add(task.id)

    try {
      let videoFile: File

      switch (task.type) {
        case 'story': {
          console.log('Generating strip video for task:', task.id)
          // CRITICAL: countdown should be in milliseconds (converted from seconds in Select.tsx)
          // Default to 3 seconds = 3000ms if not provided
          const countdownMs = task.countdown || 3000
          videoFile = await generateFramedVideo(
            task.resources,
            task.frameConfig,
            1,
            task.sessionId,
            FRAMED_EXPORT_RECORDING_SPEED,
            task.fps || 30,
            task.photoStates,
            countdownMs
          )
          console.log('Strip video generated successfully for task:', task.id)
          break
        }

        case 'live': {
          console.log('Generating live mode video for task:', task.id)
          videoFile = await generateLiveMode(
            task.resources,
            task.frameConfig,
            1,
            task.sessionId,
            FRAMED_EXPORT_RECORDING_SPEED,
            task.fps || 30,
            task.photoStates,
            task.photoDuration || 100
          )
          console.log('Live mode video generated successfully for task:', task.id)
          break
        }

        case 'session': {
          if (!task.videoFile) {
            const errorMsg = 'Video file missing for capture task'
            useSessionStateStore.getState().logActivity(
              `Video task failed: ${errorMsg}`,
              {
                taskId: task.id,
                type: task.type,
                level: 'error'
              },
              undefined,
              task.sessionId
            )
            throw new Error(errorMsg)
          }
          console.log('Using capture video file for task:', task.id)
          videoFile = task.videoFile
          break
        }

        default: {
          const errorMsg = 'Invalid video processing task type'
          useSessionStateStore.getState().logActivity(
            `Video task failed: ${errorMsg}`,
            {
              taskId: task.id,
              type: task.type,
              level: 'error'
            },
            undefined,
            task.sessionId
          )
          throw new Error(errorMsg)
        }
      }

      console.log('Starting video download for task:', task.id)
      const localUrl = URL.createObjectURL(videoFile)
      if (task.type === 'story' || task.type === 'live') {
        this.downloadVideoLocally(localUrl, task.id, task.type, task.sessionId)
      }

      // Check network status and API availability to determine upload path
      const isOnline = await checkNetworkStatus()
      const hasPreload = hasPreloadAPI()

      if (!isOnline && hasPreload) {
        console.log('Saving video to offline storage for task:', task.id)
        const offlineResult = await saveMediaOffline({
          sessionId: task.sessionId,
          fileName: videoFile.name,
          file: videoFile,
          fileType: 'videos',
          metadata: {
            mimeType: videoFile.type || 'video/mp4'
            // Note: Video dimensions and duration would need to be extracted if available
          }
        })

        if (offlineResult.success) {
          console.log(
            'Video saved to offline storage:',
            offlineResult.path,
            offlineResult.resourceId
          )
          // Offline pipeline handles upload when online - call onComplete with local URL only
          if (task.onComplete) {
            task.onComplete('', localUrl) // Empty URL since upload happens later via background job manager
          }
          this.activeTasks.delete(task.id)
          this.processNextTask()
          return
        } else if (offlineResult.skipped) {
          console.log(
            '[VideoProcessor] Offline storage not available; falling back to uploadManager for task:',
            task.id
          )
          // Fall through to uploadManager for IndexedDB retry
        } else {
          console.error('Failed to save video to offline storage:', offlineResult.error)
          // Fall through to uploadManager for IndexedDB retry
        }
      }

      // Online OR offline without preload API: Use uploadManager
      // uploadManager handles both direct upload (online) and IndexedDB retry (offline without API)
      console.log('Starting video upload for task:', task.id)
      const { promise } = await uploadManager.addTask(
        videoFile,
        task.userId,
        task.projectId,
        task.sessionId,
        task.galleryId,
        'recording'
      )

      // Handle upload completion — always notify onComplete so gallery gets a playable URL
      // (remote and/or blob). Previously we skipped onComplete when response.url was missing,
      // which left Result on the still strip even though the file was generated.
      promise
        .then(async (response) => {
          const remote = (response?.url && response.url.trim()) || ''
          console.log('Video upload completed for task:', task.id, {
            hasRemote: Boolean(remote),
            hasLocalBlob: Boolean(localUrl)
          })
          if (task.onComplete) {
            task.onComplete(remote, localUrl)
          }
        })
        .catch((error) => {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error'
          console.error('[VideoProcessor] Video upload failed for task:', task.id, {
            error: errorMsg,
            taskType: task.type,
            sessionId: task.sessionId,
            galleryId: task.galleryId
          })
          useSessionStateStore.getState().logActivity(
            `Video upload failed for task ${task.id}: ${errorMsg}`,
            {
              taskId: task.id,
              taskType: task.type,
              error: errorMsg,
              sessionId: task.sessionId,
              galleryId: task.galleryId,
              level: 'error'
            },
            undefined,
            task.sessionId
          )
          if (task.onComplete && localUrl) {
            task.onComplete('', localUrl)
          }
          if (task.onError) {
            task.onError(error instanceof Error ? error : new Error(errorMsg))
          }
        })
        .finally(() => {
          this.activeTasks.delete(task.id)
          this.processNextTask()
        })
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      const errorStack = error instanceof Error ? error.stack : undefined
      console.error('[VideoProcessor] Error processing video task:', task.id, {
        error: errorMsg,
        errorStack,
        taskType: task.type,
        sessionId: task.sessionId,
        galleryId: task.galleryId,
        resourcesCount: task.resources.length
      })
      useSessionStateStore.getState().logActivity(
        `Error processing video task ${task.id}: ${errorMsg}`,
        {
          taskId: task.id,
          type: task.type,
          error: errorMsg,
          errorStack,
          sessionId: task.sessionId,
          galleryId: task.galleryId,
          resourcesCount: task.resources.length,
          level: 'error'
        },
        undefined,
        task.sessionId
      )
      if (task.onError) {
        task.onError(error instanceof Error ? error : new Error(errorMsg))
      }
      this.activeTasks.delete(task.id)
      this.processNextTask()
    }
  }
}

export const backgroundVideoProcessor = BackgroundVideoProcessor.getInstance()
